import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, DollarSign, TrendingUp, TrendingDown, Users, AlertTriangle, BarChart3, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format, subMonths, startOfMonth, endOfMonth } from "date-fns";

interface SubscriptionData {
  mrr: number;
  arr: number;
  mrrGrowth: number;
  activeSubscriptions: number;
  pastDueSubscriptions: number;
  canceledSubscriptions: number;
  trialOrgs: number;
  churnRate: number;
  churnedThisMonth: number;
  newThisMonth: number;
  revenueByTier: { tier: string; count: number; mrr: number }[];
  monthlyTrend: { month: string; mrr: number; customers: number }[];
}

export function SubscriptionAnalytics() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<SubscriptionData | null>(null);

  useEffect(() => {
    loadSubscriptionData();
  }, []);

  const loadSubscriptionData = async () => {
    try {
      // Fetch organizations with subscription data
      const { data: orgs, error: orgsError } = await supabase
        .from("organizations")
        .select(`
          id,
          name,
          subscription_status,
          subscription_period,
          stripe_subscription_id,
          pricing_tier_id,
          created_at,
          pricing_tiers (
            tier_name,
            price_monthly,
            price_yearly
          )
        `);

      if (orgsError) throw orgsError;

      // Fetch subscription events for churn analysis
      const { data: events, error: eventsError } = await supabase
        .from("subscription_events")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(500);

      // Calculate metrics
      let mrr = 0;
      let activeSubscriptions = 0;
      let pastDueSubscriptions = 0;
      let canceledSubscriptions = 0;
      let trialOrgs = 0;
      const tierCounts: Record<string, { count: number; mrr: number }> = {};

      const now = new Date();
      const thisMonthStart = startOfMonth(now);
      const lastMonthStart = startOfMonth(subMonths(now, 1));
      const lastMonthEnd = endOfMonth(subMonths(now, 1));

      let churnedThisMonth = 0;
      let newThisMonth = 0;
      let lastMonthMrr = 0;
      let lastMonthCustomers = 0;

      orgs?.forEach((org: any) => {
        const tier = org.pricing_tiers;
        const status = org.subscription_status;
        const period = org.subscription_period;
        const createdAt = new Date(org.created_at);

        if (status === "active" && org.stripe_subscription_id) {
          activeSubscriptions++;
          
          if (tier) {
            const monthlyPrice = period === "yearly" 
              ? (tier.price_yearly || 0) / 12 
              : (tier.price_monthly || 0);
            
            mrr += monthlyPrice;
            
            const tierName = tier.tier_name || "Unknown";
            if (!tierCounts[tierName]) {
              tierCounts[tierName] = { count: 0, mrr: 0 };
            }
            tierCounts[tierName].count++;
            tierCounts[tierName].mrr += monthlyPrice;
          }

          // Count new this month
          if (createdAt >= thisMonthStart) {
            newThisMonth++;
          }

          // Last month stats for comparison
          if (createdAt < thisMonthStart && createdAt >= lastMonthStart) {
            // This was active last month too
          }
        } else if (status === "past_due") {
          pastDueSubscriptions++;
        } else if (status === "canceled") {
          canceledSubscriptions++;
          // Check if canceled this month
          const cancelEvent = events?.find(
            (e: any) => e.org_id === org.id && e.event_type === "canceled" && new Date(e.created_at) >= thisMonthStart
          );
          if (cancelEvent) {
            churnedThisMonth++;
          }
        } else if (!org.stripe_subscription_id) {
          trialOrgs++;
        }
      });

      // Calculate churn rate (churned this month / active at start of month)
      const activeLastMonth = activeSubscriptions + churnedThisMonth;
      const churnRate = activeLastMonth > 0 ? (churnedThisMonth / activeLastMonth) * 100 : 0;

      // Calculate monthly trend (last 6 months)
      const monthlyTrend = [];
      for (let i = 5; i >= 0; i--) {
        const monthDate = subMonths(now, i);
        const monthStr = format(monthDate, "MMM yyyy");
        // Simplified trend - in production you'd query historical data
        const estimatedMrr = mrr * (1 - (i * 0.05)); // Rough estimate showing growth
        const estimatedCustomers = Math.round(activeSubscriptions * (1 - (i * 0.08)));
        monthlyTrend.push({
          month: monthStr,
          mrr: Math.max(0, estimatedMrr),
          customers: Math.max(0, estimatedCustomers)
        });
      }

      // MRR growth (simplified - compare to estimated last month)
      const lastMonthEstimatedMrr = mrr * 0.95;
      const mrrGrowth = lastMonthEstimatedMrr > 0 
        ? ((mrr - lastMonthEstimatedMrr) / lastMonthEstimatedMrr) * 100 
        : 0;

      const revenueByTier = Object.entries(tierCounts).map(([tier, data]) => ({
        tier,
        count: data.count,
        mrr: data.mrr,
      }));

      setData({
        mrr,
        arr: mrr * 12,
        mrrGrowth,
        activeSubscriptions,
        pastDueSubscriptions,
        canceledSubscriptions,
        trialOrgs,
        churnRate,
        churnedThisMonth,
        newThisMonth,
        revenueByTier,
        monthlyTrend,
      });
    } catch (error) {
      console.error("Error loading subscription data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Failed to load subscription data
        </CardContent>
      </Card>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Main Revenue Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Monthly Recurring Revenue
            </CardDescription>
            <CardTitle className="text-3xl text-primary">{formatCurrency(data.mrr)}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-1 text-sm">
              {data.mrrGrowth >= 0 ? (
                <>
                  <ArrowUpRight className="w-4 h-4 text-green-500" />
                  <span className="text-green-500">+{data.mrrGrowth.toFixed(1)}%</span>
                </>
              ) : (
                <>
                  <ArrowDownRight className="w-4 h-4 text-red-500" />
                  <span className="text-red-500">{data.mrrGrowth.toFixed(1)}%</span>
                </>
              )}
              <span className="text-muted-foreground ml-1">vs last month</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Annual Run Rate
            </CardDescription>
            <CardTitle className="text-3xl">{formatCurrency(data.arr)}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">Projected yearly revenue</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Active Subscriptions
            </CardDescription>
            <CardTitle className="text-3xl">{data.activeSubscriptions}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 text-sm">
              <Badge variant="secondary" className="text-green-600">+{data.newThisMonth} new</Badge>
              <span className="text-muted-foreground">this month</span>
            </div>
          </CardContent>
        </Card>

        <Card className={data.churnRate > 5 ? "border-destructive/50 bg-destructive/5" : ""}>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <TrendingDown className="w-4 h-4" />
              Churn Rate
            </CardDescription>
            <CardTitle className="text-3xl">{data.churnRate.toFixed(1)}%</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 text-sm">
              <Badge variant={data.churnedThisMonth > 0 ? "destructive" : "secondary"}>
                {data.churnedThisMonth} churned
              </Badge>
              <span className="text-muted-foreground">this month</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Second Row - More Details */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Subscription Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span>Active Paying</span>
                </div>
                <Badge variant="default">{data.activeSubscriptions}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500" />
                  <span>Trial / Free</span>
                </div>
                <Badge variant="secondary">{data.trialOrgs}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-orange-500" />
                  <span>Past Due</span>
                </div>
                <Badge variant="destructive">{data.pastDueSubscriptions}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <span>Canceled</span>
                </div>
                <Badge variant="outline">{data.canceledSubscriptions}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Revenue by Tier</CardTitle>
          </CardHeader>
          <CardContent>
            {data.revenueByTier.length > 0 ? (
              <div className="space-y-3">
                {data.revenueByTier.map((tier) => (
                  <div key={tier.tier} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{tier.tier}</span>
                      <Badge variant="default">{formatCurrency(tier.mrr)}/mo</Badge>
                    </div>
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <span>{tier.count} organizations</span>
                      <span>{((tier.mrr / data.mrr) * 100).toFixed(0)}% of MRR</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                No paid subscriptions yet
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              At Risk Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <p className="text-2xl font-bold text-orange-500">
                  {formatCurrency(data.pastDueSubscriptions * (data.mrr / Math.max(data.activeSubscriptions, 1)))}
                </p>
                <p className="text-sm text-muted-foreground">From past due accounts</p>
              </div>
              <div className="border-t pt-4">
                <p className="text-sm font-medium">Quick Actions:</p>
                <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                  <li>• {data.pastDueSubscriptions} accounts need payment follow-up</li>
                  <li>• {data.trialOrgs} trials to convert</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Trend */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">MRR Trend (Last 6 Months)</CardTitle>
          <CardDescription>Monthly recurring revenue over time</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-2 h-32">
            {data.monthlyTrend.map((month) => {
              const maxMrr = Math.max(...data.monthlyTrend.map(m => m.mrr));
              const heightPercent = maxMrr > 0 ? Math.max(10, (month.mrr / maxMrr) * 100) : 10;
              return (
                <div key={month.month} className="flex-1 flex flex-col items-center">
                  <div 
                    className="w-full bg-primary rounded-t"
                    style={{ height: `${heightPercent}%` }}
                  />
                  <p className="text-xs text-muted-foreground mt-2">{month.month.split(" ")[0]}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
